import type { ExamEventData } from '@exam-center/exam-event-management';
import { adjustTime, dayjs } from '@shared/utils';

import type { ExamEventCourse } from '../types';

type ExamEventSession = ExamEventData['sessions'][number];

export function getAdjustedSessionTime(session: ExamEventSession, fallbackTimeAdjustmentInMs = 0) {
  const timeAdjustment = session.postponeDurationInMs ?? fallbackTimeAdjustmentInMs;

  return {
    timeAdjustment,
    startedAt: adjustTime(session.startedAt, timeAdjustment),
    endedAt: adjustTime(session.endedAt, timeAdjustment),
  };
}

export function getSessionLatestCourseCompletionTime(
  participatedCourses: ExamEventCourse[],
  session: ExamEventSession,
  fallbackTimeAdjustmentInMs = 0,
) {
  const { startedAt, endedAt } = getAdjustedSessionTime(session, fallbackTimeAdjustmentInMs);
  const courseDurations = participatedCourses
    .map(
      (course) =>
        course.metadata?.settings.examDuration?.sessions.find(
          (examDurationSession) => examDurationSession.order === session.round,
        )?.value,
    )
    .filter((duration): duration is number => typeof duration === 'number');

  if (courseDurations.length === 0) {
    return endedAt;
  }

  return dayjs(startedAt)
    .add(Math.max(...courseDurations), 'seconds')
    .toDate();
}

export function isExamSessionToday(
  session: ExamEventSession,
  fallbackTimeAdjustmentInMs = 0,
  now = new Date(),
) {
  const { startedAt } = getAdjustedSessionTime(session, fallbackTimeAdjustmentInMs);

  return dayjs(startedAt).isSame(now, 'day');
}

export function findActiveExamSession(
  examEvent: ExamEventData,
  fallbackTimeAdjustmentInMs = 0,
  now = new Date(),
) {
  return examEvent.sessions.find((session) => {
    const { startedAt, endedAt } = getAdjustedSessionTime(session, fallbackTimeAdjustmentInMs);

    return dayjs(now).isBetween(startedAt, endedAt, null, '[]');
  });
}

export function getLatestCourseCompletionTimeForToday(
  participatedCourses: ExamEventCourse[],
  sessions: ExamEventData['sessions'],
  fallbackTimeAdjustmentInMs = 0,
  now = new Date(),
) {
  const todaySessions = sessions.filter((session) =>
    isExamSessionToday(session, fallbackTimeAdjustmentInMs, now),
  );

  if (todaySessions.length === 0) {
    return null;
  }

  return todaySessions.reduce<Date | null>((latestCompletionTime, session) => {
    const sessionLatestCompletionTime = getSessionLatestCourseCompletionTime(
      participatedCourses,
      session,
      fallbackTimeAdjustmentInMs,
    );

    if (!latestCompletionTime || sessionLatestCompletionTime > latestCompletionTime) {
      return sessionLatestCompletionTime;
    }

    return latestCompletionTime;
  }, null);
}

export function shouldHideSessionTimeExtension(
  participatedCourses: ExamEventCourse[],
  session: ExamEventSession,
  fallbackTimeAdjustmentInMs = 0,
  now = new Date(),
) {
  if (!isExamSessionToday(session, fallbackTimeAdjustmentInMs, now)) {
    return true;
  }

  const latestCourseCompletionTime = getSessionLatestCourseCompletionTime(
    participatedCourses,
    session,
    fallbackTimeAdjustmentInMs,
  );

  return now > latestCourseCompletionTime;
}

export function shouldDisableSessionTimeExtension(
  session: ExamEventSession,
  fallbackTimeAdjustmentInMs = 0,
  now = new Date(),
) {
  const { startedAt, endedAt } = getAdjustedSessionTime(session, fallbackTimeAdjustmentInMs);

  return !dayjs(now).isBetween(startedAt, endedAt, null, '[]');
}
